import React, { ErrorInfo, FC, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { CalcReversalResult } from '../api/selfApi'
import '../assets/css/highchartsContainer.css'
import { makeChart, resetChart } from '../js/calcReversalChart'
import { propertiesIsNaN } from '../js/utils'
import { AppState } from '../store'

const chartId = 'calcReversalChart'

export interface ErrorBoundaryProps {
  notifyError?: (error: Error, info: ErrorInfo) => void
}

interface CalcReversalChartProps {
  isResponsive: boolean
}

interface ChartSeries {
  type: string
  name: string
  color: string
  data: { x: number; y: number }[]
}

function makeChartSeries(result: CalcReversalResult): ChartSeries[] | null {
  if (propertiesIsNaN(result)) return null
  return [
    {
      type: 'line',
      name: '運用しない場合',
      color: '#5a435a',
      data: result.depositList.map((value, idx) => {
        return {
          x: idx,
          y: value,
        }
      }),
    },
    {
      type: 'line',
      name: 'パターン1',
      color: '#92cfdf',
      data: result.amountList1.map((value, idx) => {
        return {
          x: idx,
          y: value,
        }
      }),
    },
    {
      type: 'line',
      name: 'パターン2',
      color: '#ffdd9d',
      data: result.amountList2.map((value, idx) => {
        return {
          x: idx,
          y: value,
        }
      }),
    },
  ]
}

export const CalcReversalChart: FC<CalcReversalChartProps> = React.memo((props) => {
  const result = useSelector((state: AppState) => state.calcReversal.result)
  const detectionWidth = useRef(window.innerWidth)
  const responseCode = useSelector((state: AppState) => state.calcReversal.responseCode)

  useEffect(() => {
    function handleResize() {
      setTimeout(() => {
        const newWidth = window.innerWidth
        if (detectionWidth.current !== newWidth) {
          detectionWidth.current = newWidth
          if (result) {
            makeChart(
              chartId,
              makeChartSeries(result),
              result.startAge,
              result.turningAge,
              result.interest1,
              result.interest2,
              props.isResponsive
            )
          }
        }
      }, 500)
    }
    if (responseCode === -4) {
      alert(
        '計算結果が上限を超えたため、評価額が目標額を達成できません。ご利用方法を確認してください。'
      )
      resetChart()
      return window.removeEventListener('resize', () => handleResize())
    } else if (result) {
      makeChart(
        chartId,
        makeChartSeries(result),
        result.startAge,
        result.turningAge,
        result.interest1,
        result.interest2,
        props.isResponsive
      )
      if (props.isResponsive) {
        window.addEventListener('resize', () => handleResize())
        return window.removeEventListener('resize', () => handleResize())
      }
    } else {
      resetChart()
      return window.removeEventListener('resize', () => handleResize())
    }
  }, [result, responseCode, props.isResponsive])
  return (
    <div className="u-mt30px">
      <div id={chartId} />
      <p className="c-note">
        保有資産の初期入力値（2,444万円）は、シニア世代約3,000名のアンケート調査
        <sup>※</sup>における平均金融資産
      </p>
      <p className="c-note">
        ※
        金融ジェロントロジーにおける資産運用に関する調査（2017年11月:野村アセットマネジメント・野村資本市場研究所）
      </p>
    </div>
  )
})
